import {
  showPopup,
  showAiPopup,
} from '@/components/editor/services/lib/commandSuggestion/commands';
import { MenuItem, MenuItemFn } from './menu-item';
import { isInTable } from '@tiptap/pm/tables';
import { nanoid } from 'nanoid';
import EventBus from '@/utilities/eventBus';
import QuickCardButton from '@/components/editor/toolbar/sections/QuickCardButton.vue';
import { ComponentPublicInstance } from 'vue';
import { DOMSerializer } from '@tiptap/pm/model';

export const listCommands: MenuItemFn = ({ editor, translate }) => [
  {
    icon: 'check-square',
    effect: () => (editor.chain().focus() as any).toggleTaskList().run(),
    tooltip: translate('menu.check-list'),
    active: () => editor.activeButtonStates.taskList,
  },
  {
    icon: 'list',
    effect: () => (editor.chain().focus() as any).toggleBulletList().run(),
    tooltip: translate('menu.bullet-list'),
    active: () => editor.activeButtonStates.bulletList,
  },
  {
    icon: 'numbered-list',
    effect: () => (editor.chain().focus() as any).toggleOrderedList().run(),
    tooltip: translate('menu.ordered-list'),
    active: () => editor.activeButtonStates.orderedList,
  },
  {
    icon: 'minus',
    effect: () => (editor.chain().focus() as any).setHorizontalRule().run(),
    tooltip: translate('menu.horizontal-rule'),
    disabled: () => editor.disabledButtonStates.isInTable,
  },
];

export const headingCommands: MenuItemFn = ({ editor, translate }) => [
  {
    icon: 'H1',
    effect: () => (editor.chain().focus() as any).toggleHeading({ level: 1 }).run(),
    active: () => editor.activeButtonStates.h1,
    tooltip: translate('menu.heading', { level: '1' }),
  },
  {
    icon: 'H2',
    effect: () => (editor.chain().focus() as any).toggleHeading({ level: 2 }).run(),
    active: () => editor.activeButtonStates.h2,
    tooltip: translate('menu.heading', { level: '2' }),
  },
  {
    icon: 'H3',
    effect: () => (editor.chain().focus() as any).toggleHeading({ level: 3 }).run(),
    active: () => editor.activeButtonStates.h3,
    tooltip: translate('menu.heading', { level: '3' }),
  },
];

export const textStyleCommands: MenuItemFn = ({ editor, translate }) => [
  {
    icon: 'bold-st',
    effect: () => (editor.chain().focus() as any).toggleBold().run(),
    active: () => editor.activeButtonStates.bold,
    tooltip: translate('menu.bold'),
    tooltipMeta: { icon: { ctrl: true, key: 'B' } },
  },
  {
    icon: 'italic-st',
    effect: () => (editor.chain().focus() as any).toggleItalic().run(),
    active: () => editor.activeButtonStates.italic,
    tooltip: translate('menu.italic'),
    tooltipMeta: { icon: { ctrl: true, key: 'I' } },
  },
  {
    icon: 'strikethrough',
    effect: () => (editor.chain().focus() as any).toggleStrike().run(),
    active: () => editor.activeButtonStates.strike,
    tooltip: translate('menu.strikethrough'),
    tooltipMeta: { icon: { modifiers: ['ctrl', 'shift'], key: 'X' } },
  },
  {
    icon: 'underline-st',
    effect: () => (editor.chain().focus() as any).toggleUnderline().run(),
    active: () => editor.activeButtonStates.underline,
    tooltip: translate('menu.underline'),
    tooltipMeta: { icon: { ctrl: true, key: 'U' } },
  },
];

// used to add align dropdown button inside of bubble menu
export const alignCommand: MenuItemFn = ({ editor }) => ({
  icon: () => {
    const { activeButtonStates } = editor;

    if (activeButtonStates.textAlignCenter) return 'align-center';
    if (activeButtonStates.textAlignRight) return 'align-right';
    if (activeButtonStates.textAlignJustified) return 'align-justify-st';
    return 'align-left';
  },
  effect: () => {},
  active: () => false,
  disabled: () => {
    // disable align button if lists are active
    if (
      editor.activeButtonStates.orderedList ||
      editor.activeButtonStates.bulletList ||
      editor.activeButtonStates.taskList
    ) {
      return true;
    }
    // enable align button if heading or paragraph is active
    return !(editor.activeButtonStates.heading || editor.activeButtonStates.paragraph);
  },
});

// actuall align commands
export const alignCommands: MenuItemFn = ({ editor, translate }) => [
  {
    icon: 'align-left',
    effect: () => (editor.chain().focus() as any).setTextAlign('left').run(),
    active: () => editor.activeButtonStates.textAlignLeft,
    tooltip: translate('menu.align-left'),
    tooltipMeta: { icon: { modifiers: ['ctrl', 'shift'], key: 'L' } },
  },
  {
    icon: 'align-center',
    effect: () => {
      (editor.chain().focus() as any).setTextAlign('center').run();
    },
    active: () => editor.activeButtonStates.textAlignCenter,
    tooltip: translate('menu.align-center'),
    tooltipMeta: { icon: { modifiers: ['ctrl', 'shift'], key: 'E' } },
  },
  {
    icon: 'align-justify-st',
    effect: () => {
      (editor.chain().focus() as any).setTextAlign('justify').run();
    },
    active: () => editor.activeButtonStates.textAlignJustified,
    tooltip: translate('menu.align-justify'),
    tooltipMeta: { icon: { modifiers: ['ctrl', 'shift'], key: 'J' } },
  },
  {
    icon: 'align-right',
    effect: () => (editor.chain().focus() as any).setTextAlign('right').run(),
    active: () => editor.activeButtonStates.textAlignRight,
    tooltip: translate('menu.align-right'),
    tooltipMeta: { icon: { modifiers: ['ctrl', 'shift'], key: 'R' } },
  },
];

export const bubbleMediaCommands: MenuItemFn = ({ editor, translate }) => [
  {
    icon: 'link-st',
    effect: () => {
      if (editor.isActive('link')) {
        editor.chain().focus().unsetLink().run();
        return;
      }
      const { state } = editor;
      const { selection } = state;

      const { from, to } = selection;
      const text = state.doc.textBetween(from, to, ' ');
      showPopup({
        editorId: editor.id,
        editor,
        insertLink: true,
        selectedText: text,
        from: editor.state.selection.from,
        to: editor.state.selection.to,
      });
    },
    active: () => editor.activeButtonStates.link,
    tooltip: translate('menu.link'),
  },
];

export const mediaCommands: MenuItemFn = ({ editor, translate, store }) => [
  ...(bubbleMediaCommands({ editor, translate, store }) as MenuItem[]),
  {
    icon: 'image-st',
    effect: () => {
      store.dispatch('setPageUploadPopup', {
        id: 'page-insert-image',
        title: translate('quickPage.title.attachImageFrom'),
        editorId: (editor as any).id,
      });
    },
  },
  {
    icon: 'youtube',
    effect: () => {
      const { state } = editor;
      const { selection } = state;

      const { from, to } = selection;
      showPopup({
        editorId: editor.id,
        youtubeVideoPopup: true,
        from,
        to,
      });
    },
    disabled: () => isInTable(editor.state),
  },
];

export const blockElementsCommands: MenuItemFn = ({ editor, translate }) => [
  {
    icon: 'blocquote',
    effect: () => {
      editor.chain().focus().toggleBlockquote().run();
    },
    tooltip: translate('menu.blockquote'),
    active: () => editor.activeButtonStates.blockquote,
    disabled: () => editor.disabledButtonStates.isInTable,
  },
  {
    icon: 'code-st',
    effect: () => {
      editor.chain().focus().toggleCode().run();
    },
    tooltip: translate('menu.code'),
    active: () => editor.activeButtonStates.code,
    disabled: () => editor.disabledButtonStates.isInTable,
  },
];

export const inlineCommentCommands: MenuItemFn = ({ editor, translate, store }) => [
  {
    icon: 'add-comment',
    effect: () => {
      const highlightSelectionId = nanoid(6);
      editor.commands.setPreCommentHighlight({
        highlight: highlightSelectionId,
        preCommentRange: editor.state.selection,
      });
      const highlightedNodes = document.querySelectorAll(
        `[data-highlighted="${highlightSelectionId}"]`
      );
      if (!highlightedNodes.length) return;
      const lastNode = highlightedNodes[highlightedNodes.length - 1];
      const rect = lastNode?.getBoundingClientRect();

      store.dispatch('setActiveComment', '');

      EventBus.$emit('displayInlineCommentInput', {
        rect,
        text: lastNode?.innerHTML,
        editorId: editor.id,
      });
    },
    tooltip: translate('menu.add-comment'),
    label: translate('comment'),
  },
];

export const cardFromSelectionCommands: MenuItemFn = ({ editor, translate, store }) => [
  {
    icon: 'plus',
    text: translate('card'),
    effect: () => {
      const { view, state } = editor;
      const { from, to } = view.state.selection;
      const text = state.doc.textBetween(from, to, ' ');

      editor.commands.setTextSelection(from);
      editor.commands.setTextSelection({ from, to });

      // Note: A $nextTick was used here originally.

      store.dispatch('setQuickCardTitle', { title: text });
      store.dispatch('setEditorForCardLink', editor.id);
      store.dispatch('setQuickCard', true);
    },
    tooltip: translate('menu.quick-card'),
    customButton: QuickCardButton as ComponentPublicInstance,
  },
];

export const undoRedoCommands: MenuItemFn = ({ editor, translate }) => [
  {
    icon: 'undo',
    effect: () => editor.chain().focus().undo().run(),
    disabled: () => {
      if (Object.keys(editor).length > 0) {
        return editor.disabledButtonStates.undo;
      }
      return false;
    },
    tooltip: translate('menu.undo'),
    tooltipMeta: { icon: { ctrl: true, key: 'Z' } },
  },
  {
    icon: 'redo',
    effect: () => editor.chain().focus().redo().run(),
    disabled: () => {
      if (Object.keys(editor).length > 0) {
        return editor.disabledButtonStates.redo;
      }
      return false;
    },
    tooltip: translate('menu.redo'),
    tooltipMeta: { icon: { ctrl: true, key: 'Y' } },
  },
];

export const showAiPopupCommand: MenuItemFn = ({ editor, translate, appendTo }) => [
  {
    icon: 'zap',
    effect: () => {
      const serializer = DOMSerializer.fromSchema(editor.view.state.schema);
      const selectedHtml = serializer.serializeFragment(
        editor.view.state.selection.content().content
      );

      // Create a temporary div element to obtain the outerHTML
      const tempDiv = document.createElement('div');
      tempDiv.appendChild(selectedHtml);

      const lastChild = tempDiv.lastChild;
      // Remove the last child if it is a blank node
      if (lastChild?.nodeType === 1 && lastChild?.textContent === '') {
        lastChild?.remove();
      }

      // Get the HTML string
      const selectedHtmlString = tempDiv.innerHTML;

      showAiPopup({
        editor,
        editorId: editor.id,
        originalText: selectedHtmlString,
        appendTo,
      });
    },
    label: translate('assistant'),
    tooltip: `AI ${translate('assistant')}`,
  },
];

export const selectionCommands = [];
